import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { formatCurrency, formatCurrencyShort } from '../lib/format.js';

const TODAY = new Date().toISOString().slice(0, 10);

function formatXLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const isneg = entry.value < 0;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[160px]">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      <p className={`font-medium tabular-nums ${isneg ? 'text-red-600' : 'text-green-700'}`}>
        {formatCurrency(entry.value)}
      </p>
    </div>
  );
}

/**
 * Graphique d'évolution du solde de caisse.
 *
 * Props :
 *   series — [{ date: 'YYYY-MM-DD', balance_cents: number }]
 */
export default function PettyCashBalanceChart({ series = [] }) {
  if (!series.length) {
    return (
      <div className="flex items-center justify-center text-sm text-slate-400" style={{ height: 180 }}>
        Pas de données
      </div>
    );
  }

  const chartData = series.map((pt) => ({
    label: formatXLabel(pt.date),
    date: pt.date,
    balance: pt.balance_cents,
  }));

  // Référence visuelle "aujourd'hui"
  const todayEntry = chartData.find((d) => d.date >= TODAY);
  const todayLabel = todayEntry?.label ?? null;

  // Couleur de la ligne selon si le solde final est positif ou négatif
  const lastBalance = chartData[chartData.length - 1]?.balance ?? 0;
  const lineColor = lastBalance >= 0 ? '#16a34a' : '#dc2626';

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={180} className="md:!h-[280px]">
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />

          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />

          <YAxis
            tickFormatter={(v) => formatCurrencyShort(v)}
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            width={60}
          />

          <Tooltip content={<CustomTooltip />} />

          {/* Ligne zéro */}
          <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={1} strokeDasharray="3 3" />

          {todayLabel && (
            <ReferenceLine
              x={todayLabel}
              stroke="#6366f1"
              strokeDasharray="4 3"
              strokeWidth={1.5}
              label={{
                value: 'Auj.',
                position: 'insideTopRight',
                fontSize: 10,
                fill: '#6366f1',
              }}
            />
          )}

          <Line
            type="monotone"
            dataKey="balance"
            name="Solde caisse"
            stroke={lineColor}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
